import React, { useState, useId, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export type TooltipProps = {
  content: string;
  children: React.ReactElement;
  placement?: 'top' | 'bottom';
  delayMs?: number;
  usePortal?: boolean; // neu: verhindert Abschneiden durch overflow
  maxWidthPx?: number; // optional
};

// Einfacher, zugänglicher Tooltip: zeigt sich bei Hover oder Fokus.
// Nutzung: <Tooltip content="Text"><button>...</button></Tooltip>
export function Tooltip({ content, children, placement = 'top', delayMs = 120, usePortal = true, maxWidthPx = 220 }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  type TimerHandle = ReturnType<typeof setTimeout>;
  const [timer, setTimer] = useState<TimerHandle | null>(null);
  const id = useId();
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const clearTimer = () => { if (timer) clearTimeout(timer); };
  const schedule = (fn: () => void) => { clearTimer(); const t: TimerHandle = setTimeout(fn, delayMs); setTimer(t); };

  const show = () => schedule(() => setVisible(true));
  const hide = () => { clearTimer(); setVisible(false); };
  useEffect(() => () => clearTimer(), [timer]);

  const computeCoords = () => {
    if (!anchorRef.current) return null;
    const rect = anchorRef.current.getBoundingClientRect();
    return { top: placement === 'top' ? rect.top : rect.bottom, left: rect.left + rect.width / 2 };
  };

  useEffect(() => {
    if (visible && usePortal) setCoords(computeCoords());
  }, [visible, placement, usePortal]);

  useEffect(() => {
    if (!usePortal || !visible) return;
    const update = () => setCoords(computeCoords());
    update();
    globalThis.addEventListener('scroll', update, true);
    globalThis.addEventListener('resize', update);
    return () => {
      globalThis.removeEventListener('scroll', update, true);
      globalThis.removeEventListener('resize', update);
    };
  }, [visible, placement, usePortal]);

  const cloned = React.cloneElement(children, {
    'aria-describedby': visible ? id : undefined,
    onFocus: (e: React.FocusEvent) => { children.props.onFocus?.(e); show(); },
    onBlur: (e: React.FocusEvent) => { children.props.onBlur?.(e); hide(); },
    onMouseEnter: (e: React.MouseEvent) => { children.props.onMouseEnter?.(e); show(); },
    onMouseLeave: (e: React.MouseEvent) => { children.props.onMouseLeave?.(e); hide(); },
  });

  const tooltipInner = (
    <span
      id={id}
      role="tooltip"
      className={
        'pointer-events-none z-50 whitespace-pre-line text-xs font-medium transition-opacity duration-150 ' +
        (visible ? 'opacity-100' : 'opacity-0')
      }
      style={{ maxWidth: maxWidthPx }}
      data-testid="tooltip"
    >
      <span className="block rounded-md px-2 py-1 bg-slate-800 text-white shadow-lg shadow-slate-900/30 border border-slate-700">
        {content}
      </span>
    </span>
  );

  let renderedTooltip: React.ReactNode = null;
  if (usePortal) {
    if (visible && coords) {
      const portalNode = (
        <div
          style={{
            position: 'fixed',
            top: placement === 'top' ? coords.top - 8 : coords.top + 8,
            left: coords.left,
            transform: 'translateX(-50%)',
            pointerEvents: 'none',
          }}
        >
          {tooltipInner}
        </div>
      );
      renderedTooltip = createPortal(portalNode, document.body);
    } else if (visible && !coords) {
      // Fallback: noch keine Koordinaten berechnet – relative Platzierung
      renderedTooltip = (
        <span className={'absolute ' + (placement === 'top' ? 'bottom-full mb-2 left-1/2 -translate-x-1/2' : 'top-full mt-2 left-1/2 -translate-x-1/2')}>
          {tooltipInner}
        </span>
      );
    }
  } else {
    renderedTooltip = (
      <span className={'absolute ' + (placement === 'top' ? 'bottom-full mb-2 left-1/2 -translate-x-1/2' : 'top-full mt-2 left-1/2 -translate-x-1/2')}>
        {tooltipInner}
      </span>
    );
  }

  return (
    <span
      className="relative inline-block"
      ref={anchorRef}
      role="none"
      title={content} // Titel für einfache Tests & Fallback Tooltip
    >
      {cloned}
      {!usePortal && renderedTooltip}
      {usePortal && renderedTooltip}
    </span>
  );
}
